import { useState, useRef, useEffect } from 'react';
import { useUserIdentityState } from '@provider/user-identity';

/**
 * Hook that once extracts the src for the iframe to render, through a
 * potentially async request.
 */
function useResolvedDisplayData(getDisplayData, learningObjectId) {
  const user = useUserIdentityState(state => state.user);
  const [failedLoading, setFailed] = useState(false);
  const [resolvedDisplayData, setDisplayData] = useState(resolveDisplayData(getDisplayData(user), learningObjectId));

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const updateDisplayDataRef = useRef(() => {});
  useEffect(() => {
    updateDisplayDataRef.current = async () => {
      setFailed(false);
      try {
        const displayData = await getDisplayData(user);
        const resolvedDisplayData = resolveDisplayData(displayData, learningObjectId);

        // update displayData to trigger a new rendering of the component only
        // if the resolved data actually differs (we use JSON.stringify for
        // developer convenience in spite of the bad performance - it is, in
        // this case, negligible)
        setDisplayData(prevDisplayData => JSON.stringify(prevDisplayData) !== JSON.stringify(resolvedDisplayData) ? resolvedDisplayData : prevDisplayData);
      } catch (err) {
        console.error("Failed to load iframe display data:", err);
        setFailed(true);
      }
    };
    updateDisplayDataRef.current();
  }, [getDisplayData, learningObjectId, user]);
  return [resolvedDisplayData, failedLoading, updateDisplayDataRef.current];
}
function resolveDisplayData(displayData, learningObjectId) {
  if (displayData instanceof Promise) {
    return undefined;
  }

  // create a direct backlink to the current page, possibly with a reference
  // to the learning object in question
  const backlinkUrl = new URL(location.href);
  if (learningObjectId) {
    backlinkUrl.hash = learningObjectId;
  }

  // ... and inject the backlink into the fully qualified iframe URL, so that
  // (mainly) components can resolve where they were opened from
  const url = new URL(displayData.src, window.location.href);
  url.searchParams.append("backlinkUrl", encodeURIComponent(backlinkUrl.toString()));
  return {
    ...displayData,
    src: url.toString()
  };
}

export { useResolvedDisplayData };
//# sourceMappingURL=hook.resolve-display-data.js.map
